import { isKnowable, isKnower, knowledgeLeak, leakIgnore, USE_ROLES, type IgnoredTerm, type SpanRole, type StoryCtx, type StoryNode } from '@dispositio/core';
import type { Mark, Node as PMNode } from '@tiptap/pm/model';
import { PluginKey } from '@tiptap/pm/state';
import { suggestExtension, type SuggestLive, type SuggestState } from './suggest';

/**
 * Wissenslecks (AP-43, core `knowledgeChecks`): Eine Figur spricht von etwas oder denkt an etwas, das sie erst später erfährt – rot an
 * dieser Rede bzw. diesem Gedanken (`suggest.ts`, Art `conflict`). Benutzt wird Gewusstes, wenn der Bezug ihm zugleich gehört oder es
 * darin verknüpft ist. ✕ merkt „ist Absicht“ (sie ahnt es, jemand hat es ihr erzählt …), der weitere Ausweg zeigt, wo sie es erfährt. Nur,
 * was in Weltzeit sicher ist; nur im Draft einer Szene.
 */

export interface LeakHit {
  from: number;
  to: number;
  knower: string;
  about: string;
  role: SpanRole;
  learnedIn: string;
}

export interface LeakLive extends SuggestLive<LeakHit> {
  ctx: StoryCtx;
  scene: StoryNode | null;
  /** Entität zu einem Verweisschlüssel */
  entityOf: (key: string) => string | null;
  ignored: readonly IgnoredTerm[] | undefined;
  title: (hit: LeakHit) => string;
  other: (hit: LeakHit) => { label: string; title: string } | null;
}

export const knowledgeLeakKey = new PluginKey<SuggestState<LeakHit>>('knowledgeLeaks');

/** Rede bzw. Gedanke im Dokument: ein Bezug über mehrere Textstücke (etwa mit Verweisen darin) zählt einmal, samt den Verweisen darin */
interface SpeechSpan {
  from: number;
  to: number;
  role: SpanRole;
  keys: string[];
  linked: Set<string>;
}

function speechSpans(doc: PMNode): SpeechSpan[] {
  const out: SpeechSpan[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return true;
    const span = node.marks.find((m: Mark) => m.type.name === 'refSpan' && USE_ROLES.includes(m.attrs.role as SpanRole));
    if (!span) return false;
    const keys = span.attrs.keys as string[];
    const link = node.marks.find((m: Mark) => m.type.name === 'refLink');
    const last = out[out.length - 1];
    const same = last && last.to === pos && last.role === span.attrs.role && last.keys.join(' ') === keys.join(' ');
    const cur = same ? last : { from: pos, to: pos, role: span.attrs.role as SpanRole, keys, linked: new Set<string>() };
    cur.to = pos + node.nodeSize;
    if (link?.attrs.key) cur.linked.add(String(link.attrs.key));
    if (!same) out.push(cur);
    return false;
  });
  return out;
}

function leakHits(doc: PMNode, l: LeakLive): LeakHit[] {
  const scene = l.scene;
  if (!scene) return [];
  const ignored = (knower: string, about: string): boolean => {
    const entry = leakIgnore(l.ctx, { knower, about });
    return !!l.ignored?.some((x) => x.term === entry.term && x.before === entry.before && x.after === entry.after);
  };
  const out: LeakHit[] = [];
  for (const s of speechSpans(doc)) {
    const ids = s.keys.map(l.entityOf).filter((id): id is string => !!id);
    const knowers = ids.filter((id) => isKnower(l.ctx, id));
    const abouts = [...new Set([...ids, ...[...s.linked].map(l.entityOf)].filter((id): id is string => !!id && !knowers.includes(id) && isKnowable(l.ctx, id)))];
    const hit = knowers.flatMap((k) => abouts.map((a) => ({ k, a, leak: ignored(k, a) ? null : knowledgeLeak(l.ctx, scene, k, a) }))).find((x) => x.leak?.certainty === 'sure');
    if (hit) out.push({ from: s.from, to: s.to, knower: hit.k, about: hit.a, role: s.role, learnedIn: hit.leak!.learnedIn! });
  }
  return out;
}

export const knowledgeLeakMarks = (live: { current: LeakLive }) =>
  suggestExtension<LeakHit, LeakLive>(
    {
      name: 'knowledgeLeaks',
      kind: 'conflict',
      key: knowledgeLeakKey,
      base: 'rt-conflict',
      hits: leakHits,
      mark: (h, l) => ({ class: 'rt-knowledge-leak', 'data-knowledge-leak': h.about, title: l.title(h) }),
      labels: (_h, l) => ({ accept: null, ignore: l.labels.ignore }),
      other: (h, l) => l.other(h),
      word: (h) => h.about,
      testId: 'leak',
    },
    live,
  );
